import styled from '@emotion/styled';
import {css, IconButton, IconButtonProps, PopoverOrigin} from '@mui/material';
import React from 'react';

import {mixinFocus, mixinSVGIcon, mixinSVGIconSmall} from '@ELib/styles/mixins';
import {classyArray} from '@ELib/utilities/HelperFunctions';
import {ConditionalWrapper} from '@ELib/utilities/jsx';

import {SubMenuActionsType} from '../EMenu';
import {ETooltip} from '../ETooltip';

import {useButtonMenu} from './EButtons.hooks';

const mixinIconButton = css`
  &:disabled {
    color: var(--color-buttons-primary-inactive);
    cursor: not-allowed;
    svg {
      fill: var(--color-buttons-primary-inactive);
    }
  }

  &:enabled {
    color: var(--color-buttons-primary-active);
    cursor: pointer;
    svg {
      fill: var(--color-buttons-primary-active);
    }

    &:hover {
      color: var(--color-buttons-primary-hover);
      svg {
        fill: var(--color-buttons-primary-hover);
      }
    }

    &:focus {
      ${mixinFocus}
    }
  }
`;

export type EIconButtonProps = IconButtonProps & {
  testId?: string;
  extraActions?: SubMenuActionsType[];
  disableMenuPortal?: boolean;
  popoverProps?: {
    transformOrigin?: PopoverOrigin;
    anchorOrigin?: PopoverOrigin;
  };
};

const EIconButtonBase = ({
  onClick,
  extraActions,
  disableMenuPortal,
  testId,
  popoverProps,
  title,
  ...props
}: Omit<EIconButtonProps, 'color'>) => {
  const {menu, handleClick} = useButtonMenu({
    extraActions,
    onClick,
    disablePortal: disableMenuPortal,
    popoverProps,
  });

  return (
    <span>
      <ConditionalWrapper
        condition={Boolean(title)}
        wrapper={(children) => (
          <ETooltip title={title ?? ''}>{children}</ETooltip>
        )}
      >
        <span className='WrapperEIconButton'>
          <IconButton
            {...props}
            className={classyArray(['EIconButton', props.className])}
            data-testid={testId ? `EIconButton-${testId}` : undefined}
            disableRipple={true}
            onClick={handleClick}
          />
        </span>
      </ConditionalWrapper>
      {menu}
    </span>
  );
};

export const EIconButton = styled(EIconButtonBase)`
  ${mixinIconButton},
  &:disabled {
    color: var(--color-buttons-primary-inactive);
  }
  svg {
    ${mixinSVGIcon}
  }
`;

export const EIconSmallButton = styled(EIconButtonBase)`
  ${mixinIconButton};
  &:disabled {
    color: var(--color-buttons-primary-inactive);
  }
  svg {
    ${mixinSVGIconSmall}
  }
`;

export const NEIconButton = styled(EIconButton)`
  && {
    width: var(--button-dimensions);
    height: var(--button-dimensions);
    border-radius: var(--button-icon-border-radius);
    background-color: var(--color-core-white);

    color: var(--color-buttons-primary-active);
    border: var(--button-border-width) solid var(--color-buttons-primary-active);

    &:hover {
      color: var(--color-buttons-primary-hover);
      border-color: var(--color-buttons-primary-hover);
    }

    &:disabled {
      color: var(--color-buttons-primary-inactive);
      border-color: var(--color-buttons-primary-inactive);
    }
  }
`;

export const NETransparentIconButton = styled(NEIconButton)`
  && {
    border-color: transparent;

    &:hover {
      border-color: transparent;
    }

    &:disabled {
      border-color: transparent;
    }

    &:focus {
      border-color: var(--color-core-keyboardfocus);
    }
  }
`;
